// The one sentence a form dialog shows when a save fails, from whatever axios rejected with.
//
// Shapes it reads (most specific first):
// - 429 from @nestjs/throttler: its own text ("ThrottlerException: Too Many Requests") is not for
//   people, so a 429 always gets a plain sentence.
// - `{ message }` from the API's AllExceptionsFilter: a string, or the ValidationPipe's array of
//   sentences ("email must be an email"), joined into one paragraph.
// - `{ error: { message } }`, the older shape a few stores still read.
// Anything else (no response: network error, timeout) gets the caller's fallback, never axios's own
// "Network Error" / "Request failed with status code 500".

export const TOO_MANY_ATTEMPTS = 'Too many attempts. Wait a minute and try again.'

interface ErrorLike {
  response?: {
    status?: number
    data?: { message?: unknown; error?: { message?: unknown } | unknown }
  }
}

function sentences(message: unknown): string {
  const parts = ([] as unknown[])
    .concat(message ?? [])
    .filter((m): m is string => typeof m === 'string' && m.trim() !== '')
    .map((m) => m.trim())
  // Validation messages come without a full stop; add one between them so "a", "b" reads "a. b".
  return parts.map((m, i) => (i < parts.length - 1 && !/[.!?]$/.test(m) ? `${m}.` : m)).join(' ')
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  const response = (err as ErrorLike | null | undefined)?.response
  if (!response) return fallback
  if (response.status === 429) return TOO_MANY_ATTEMPTS
  const data = response.data
  const fromMessage = sentences(data?.message)
  if (fromMessage) return fromMessage
  const nested = data?.error
  const fromError = nested && typeof nested === 'object' ? sentences((nested as { message?: unknown }).message) : ''
  return fromError || fallback
}

/**
 * The demo org's write block: lib/api.ts turns the API's `200 { demoMode: true }` into an info toast
 * and rejects with `{ isDemoMode: true }`. The toast already told the user; a form shows nothing more.
 */
export function isDemoRejection(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { isDemoMode?: unknown }).isDemoMode === true
}
